/**
 * Pure rules for one-to-one calls. No Firebase, no WebRTC — so they can be tested.
 *
 * The live call is driven by useCallController (state + signalling) and WebRTCService
 * (the peer connection). Anything that is a DECISION rather than I/O lives here.
 */

/** Ring for this long with no answer, then it is a missed call. */
export const RING_TIMEOUT_MS = 45_000;
/** A ring older than this (by SERVER time) is stale — e.g. the caller's tab died mid-ring. */
export const STALE_RING_MS = 60_000;
/** After the network drops, wait this long for the connection to recover before giving up. */
export const DISCONNECT_GRACE_MS = 15_000;

/** Statuses that end a call. Written to calls/{id}/status. */
export const TERMINAL = new Set(['ended', 'declined', 'missed', 'busy', 'failed', 'cancelled']);

/**
 * What the CALLER is told when the call ends without them hanging up.
 * Returns null when there is nothing worth saying (they ended it themselves).
 */
export function endNotice(status, otherName = 'They') {
    const who = otherName || 'They';
    switch (status) {
        case 'declined': return { text: `${who} declined the call.`, tone: 'info' };
        case 'busy': return { text: `${who} is on another call.`, tone: 'info' };
        case 'missed': return { text: `No answer from ${who}.`, tone: 'info' };
        case 'failed': return { text: 'The call could not connect. One of you may be on a network that blocks calls.', tone: 'error' };
        default: return null;
    }
}

/** A clear message for a getUserMedia / getDisplayMedia failure. */
export function mediaErrorMessage(err, { video = false, screen = false } = {}) {
    const name = err?.name || '';
    const what = screen ? 'screen sharing' : video ? 'camera and microphone' : 'microphone';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return screen
            ? 'Screen sharing was cancelled or blocked.'
            : `Allow ${what} access for this site in your browser (the icon in the address bar), then try again.`;
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return video ? 'No camera was found on this device.' : 'No microphone was found on this device.';
    }
    if (name === 'NotReadableError' || name === 'AbortError') {
        return `Your ${what} is being used by another app. Close it and try again.`;
    }
    if (name === 'NotSupportedError' || name === 'TypeError') {
        return screen ? 'This browser cannot share the screen.' : 'This browser cannot make calls. Use Chrome or Edge.';
    }
    return `Could not start ${what}${err?.message ? `: ${err.message}` : '.'}`;
}

/** Is a ring stale? Uses server-corrected time, because client clocks drift. */
export function isStaleRing(ringTimestamp, serverNow) {
    if (typeof ringTimestamp !== 'number' || typeof serverNow !== 'number') return false;
    return serverNow - ringTimestamp > STALE_RING_MS;
}

/**
 * Extract the numbers that matter from an RTCStatsReport (or any iterable of stats).
 * @returns {{rttMs: number|null, lossPct: number|null, relayed: boolean}}
 */
export function qualityFromStats(stats) {
    const list = stats && typeof stats.values === 'function' ? [...stats.values()] : Array.isArray(stats) ? stats : [];
    const byId = new Map(list.map(s => [s.id, s]));
    let rttMs = null;
    let relayed = false;
    const pair = list.find(s => s.type === 'candidate-pair' && (s.nominated || s.selected) && s.state === 'succeeded')
        || list.find(s => s.type === 'candidate-pair' && s.state === 'succeeded');
    if (pair) {
        if (typeof pair.currentRoundTripTime === 'number') rttMs = Math.round(pair.currentRoundTripTime * 1000);
        const local = byId.get(pair.localCandidateId);
        const remote = byId.get(pair.remoteCandidateId);
        relayed = local?.candidateType === 'relay' || remote?.candidateType === 'relay';
    }
    let received = 0;
    let lost = 0;
    for (const s of list) {
        if (s.type === 'inbound-rtp' && !s.isRemote) {
            received += Number(s.packetsReceived) || 0;
            lost += Math.max(0, Number(s.packetsLost) || 0);
        }
    }
    const lossPct = received + lost > 0 ? Math.round((lost / (received + lost)) * 1000) / 10 : null;
    return { rttMs, lossPct, relayed, received, lost };
}

/** good | fair | poor | unknown — for the signal-bars indicator. */
export function classifyQuality({ rttMs, lossPct } = {}) {
    if (rttMs == null && lossPct == null) return 'unknown';
    if ((rttMs != null && rttMs > 400) || (lossPct != null && lossPct > 8)) return 'poor';
    if ((rttMs != null && rttMs > 200) || (lossPct != null && lossPct > 3)) return 'fair';
    return 'good';
}

/**
 * Loss is cumulative since the call began. To report the CURRENT state, diff two
 * samples. Returns loss over the interval, or null if nothing new arrived.
 */
export function intervalLoss(prev, next) {
    if (!prev || !next) return null;
    const recv = (next.received ?? 0) - (prev.received ?? 0);
    const lost = (next.lost ?? 0) - (prev.lost ?? 0);
    if (recv + lost <= 0) return null;
    return Math.round((Math.max(0, lost) / (recv + Math.max(0, lost))) * 1000) / 10;
}

/** The text of the system line left in the conversation once a call is over. */
export function callSummaryText({ type, outcome, durationSec }) {
    const kind = type === 'video' ? 'Video call' : 'Voice call';
    if (outcome === 'missed') return `Missed ${kind.toLowerCase()}`;
    if (outcome === 'declined') return `${kind} declined`;
    if (outcome === 'busy') return `${kind} — line busy`;
    if (outcome === 'failed') return `${kind} could not connect`;
    if (outcome === 'cancelled') return `${kind} cancelled`;
    const s = Math.max(0, Math.round(durationSec || 0));
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${kind} · ${mm}:${ss}`;
}

export const formatDuration = (secs) => {
    const s = Math.max(0, Math.floor(secs || 0));
    const h = Math.floor(s / 3600);
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};
